import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Fingerprint,
  Security,
  Smartphone,
  CheckCircle,
  Error,
  Close,
} from '@mui/icons-material';
import { pwaAuthService, PWAUser } from '../../services/pwaAuthService';
import { pwaService } from '../../services/pwaService';

interface PWAAuthModalProps {
  open: boolean;
  onClose: () => void;
  onAuthenticated: (user: PWAUser) => void;
  user?: PWAUser;
}

export const PWAAuthModal: React.FC<PWAAuthModalProps> = ({
  open,
  onClose,
  onAuthenticated,
  user,
}) => {
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    checkBiometricAvailability();
    if (user) {
      setBiometricEnabled(pwaAuthService.isBiometricEnabled());
      setRememberMe(pwaAuthService.isRememberMeEnabled());
    }
  }, [user]);

  const checkBiometricAvailability = async () => {
    try {
      const available = await pwaAuthService.isBiometricAvailable();
      setBiometricAvailable(available);
    } catch (error) {
      console.error('Error checking biometric availability:', error);
    }
  };

  const handleBiometricSetup = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const success = await pwaAuthService.enableBiometric(user);
      if (success) {
        setBiometricEnabled(true);
        setSuccess('Biometric authentication enabled successfully!');
      } else {
        setError('Failed to enable biometric authentication');
      }
    } catch (error) {
      setError('Error setting up biometric authentication');
      console.error('Biometric setup error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricAuth = async () => {
    setLoading(true);
    setError(null);

    try {
      const authenticatedUser = await pwaAuthService.authenticateWithBiometric();
      if (authenticatedUser) {
        onAuthenticated(authenticatedUser);
      } else {
        setError('Biometric authentication failed');
      }
    } catch (error) {
      setError('Error during biometric authentication');
      console.error('Biometric auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRememberMeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRememberMe(event.target.checked);
    if (user) {
      pwaAuthService.storeUserSession(user, event.target.checked);
    }
  };

  const handleContinue = () => {
    if (user) {
      pwaAuthService.storeUserSession(user, rememberMe);
      onAuthenticated(user);
    }
  };

  const isPWAInstalled = pwaService.isPWAInstalled();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {user ? 'Setup PWA Authentication' : 'PWA Authentication'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={3}>
          {!isPWAInstalled && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Install this app for the best experience with biometric authentication.
              </Typography>
            </Alert>
          )}

          {user && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Welcome, {user.name}!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure your authentication preferences for this app.
              </Typography>
            </Box>
          )}

          {/* Biometric Authentication Section */}
          <Box mb={3}>
            <Box display="flex" alignItems="center" mb={2}>
              <Fingerprint sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Biometric Authentication</Typography>
            </Box>

            {biometricAvailable ? (
              <Box>
                {user && !biometricEnabled ? (
                  <Button
                    variant="outlined"
                    startIcon={<Security />}
                    onClick={handleBiometricSetup}
                    disabled={loading}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    {loading ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : (
                      'Setup Biometric Login'
                    )}
                  </Button>
                ) : biometricEnabled ? (
                  <Box display="flex" alignItems="center" mb={2}>
                    <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="body2" color="success.main">
                      Biometric authentication enabled
                    </Typography>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<Fingerprint />}
                    onClick={handleBiometricAuth}
                    disabled={loading}
                    fullWidth
                  >
                    {loading ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : (
                      'Login with Biometric'
                    )}
                  </Button>
                )}
              </Box>
            ) : (
              <Alert severity="warning">
                <Typography variant="body2">
                  Biometric authentication is not available on this device.
                </Typography>
              </Alert>
            )}
          </Box>

          {/* Remember Me Section */}
          {user && (
            <Box mb={3}>
              <Box display="flex" alignItems="center" mb={2}>
                <Smartphone sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Session Management</Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    color="primary"
                  />
                }
                label="Remember me (stay logged in for 30 days)"
              />
            </Box>
          )}

          {/* Status Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">{success}</Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {user && (
          <Button
            onClick={handleContinue}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}; 